import React, { Fragment, useEffect, useState } from "react";
import logo from "../../../assets/media/logo.png";
import GamingBoardBahamasLogo from "../../../assets/media/GamingBoardBahamasLogo.png";
import iconMark from "../../../assets/media/icon-mark.png";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import GBInput from "../../input";
import * as Yup from "yup";
import {
  clearMfaChallenge,
  resendLoginOtp,
  verifyLoginOtp,
} from "../../../store/reducers/authSlice";
import { getHomePath } from "../../../utility/rbac";
import { showError } from "../../../helpers/toast";
import {
  formatMfaCountdown,
  getMfaCodeSecondsLeft,
} from "../../../helpers/mfaCodeTimer";

/**
 * Company portal SMS MFA step.
 * Countdown from code_expires_at / code_expires_in; Resend only after timer ends.
 * Trusted device (mfa_device_token) skips this for company admin / employee for 30 days.
 */
const LoginMfa = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const mfa = useSelector((state) => state.auth.mfa);
  const loading = useSelector((state) => state.auth.loading);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    getMfaCodeSecondsLeft(mfa?.codeExpiresAt)
  );

  const { values, handleChange, handleSubmit, errors, touched, setFieldValue } =
    useFormik({
      initialValues: { otp: "" },
      validationSchema: Yup.object({
        otp: Yup.string()
          .required("OTP is required")
          .matches(/^\d{4,8}$/, "Enter the OTP sent to your phone"),
      }),
      onSubmit: async (formValues) => {
        if (!mfa?.token) {
          showError("Login session expired. Please sign in again.");
          navigate("/");
          return;
        }
        try {
          await dispatch(
            verifyLoginOtp({
              otp: String(formValues.otp).trim(),
              mfaToken: mfa.token,
              loginEmail: mfa.loginEmail,
            })
          ).unwrap();
        } catch (err) {
          if (err?.expired) {
            showError(
              err.message ||
                "Verification session expired. Please sign in again."
            );
            dispatch(clearMfaChallenge());
            navigate("/");
          }
        }
      },
    });

  useEffect(() => {
    if (!mfa?.codeExpiresAt) {
      setSecondsLeft(0);
      return undefined;
    }
    const tick = () => {
      setSecondsLeft(getMfaCodeSecondsLeft(mfa.codeExpiresAt));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [mfa?.codeExpiresAt, mfa?.token]);

  useEffect(() => {
    if (!mfa?.token) {
      navigate("/");
    }
  }, [mfa?.token, navigate]);

  useEffect(() => {
    if (user?.user && user?.access_token) {
      navigate(getHomePath(user));
    }
  }, [navigate, user]);

  const canResend = secondsLeft <= 0;

  const handleResend = async () => {
    if (!mfa?.token || !canResend) return;
    try {
      await dispatch(resendLoginOtp({ mfaToken: mfa.token })).unwrap();
      setFieldValue("otp", "");
    } catch (err) {
      if (err?.expired) {
        showError(
          err.message || "Verification session expired. Please sign in again."
        );
        dispatch(clearMfaChallenge());
        navigate("/");
      }
    }
  };

  const handleBack = () => {
    dispatch(clearMfaChallenge());
    navigate("/");
  };

  const phoneHint = mfa?.phoneHint ? ` ${mfa.phoneHint}` : "";

  return (
    <div className="App">
      <div className="container">
        <div className="landingWrapper">
          <div className="landingRow">
            <div className="landingCol">
              <div className="fieldsWrapper">
                <img src={logo} className="logo" alt="logo" />
                <h2>Verify your identity</h2>
                <p>
                  Code sent to{phoneHint || " your registered phone number"}.
                  Enter the 6-digit code to continue signing in.
                </p>

                <form className="gb-login-form" onSubmit={handleSubmit}>
                  <Fragment>
                    <GBInput
                      name="otp"
                      type="text"
                      label="Verification code"
                      value={values.otp}
                      placeholder="Enter 6-digit code"
                      setValue={handleChange}
                      iconSrc={iconMark}
                    />
                    {touched.otp && errors.otp && (
                      <div className="error-message error-message-wrap">
                        {errors.otp}
                      </div>
                    )}
                  </Fragment>

                  <div
                    className="gb-field-wrap"
                    style={{
                      marginTop: "8px",
                      marginBottom: "4px",
                      minHeight: "24px",
                    }}
                  >
                    {!canResend ? (
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#082a51",
                        }}
                        aria-live="polite"
                      >
                        Code expires in{" "}
                        <strong>{formatMfaCountdown(secondsLeft)}</strong>
                      </p>
                    ) : (
                      <button
                        type="button"
                        className="form-submit"
                        onClick={handleResend}
                        disabled={loading}
                        style={{
                          width: "100%",
                          marginTop: "4px",
                          background: loading ? "#ccc" : "#114df2",
                          border: loading
                            ? "solid 1px #ccc"
                            : "solid 1px #1890ff",
                          boxShadow: "0px 2px 0px 0px rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        {loading ? "Sending..." : "Resend OTP"}
                      </button>
                    )}
                  </div>

                  <div className="gb-form-footer">
                    <button
                      type="submit"
                      className="form-submit"
                      disabled={loading || !values.otp}
                      style={{
                        background:
                          loading || !values.otp ? "#ccc" : undefined,
                        border:
                          loading || !values.otp
                            ? "solid 1px #ccc"
                            : undefined,
                      }}
                    >
                      {loading ? (
                        <>
                          <div
                            className="text-xs spinner-border text-light"
                            style={{ width: "1rem", height: "1rem" }}
                            role="status"
                          />
                          <span className="sr-only ml-5"> Loading...</span>
                        </>
                      ) : (
                        "Verify & Sign In"
                      )}
                    </button>
                  </div>
                </form>

                <div className="gb-form-footer" style={{ marginTop: "12px" }}>
                  <button
                    type="button"
                    onClick={handleBack}
                    style={{
                      width: "100%",
                      borderRadius: "4px",
                      border: "1px solid #082a51",
                      background: "#fff",
                      color: "#082a51",
                      padding: "8px 15px",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "22px",
                      cursor: "pointer",
                      boxShadow: "0px 1px 0px 0px rgba(0, 0, 0, 0.04)",
                    }}
                  >
                    ← Back to Sign In
                  </button>
                </div>
              </div>
            </div>
            <div className="landingCol">
              <div className="imageWrapper">
                <div className="loginBox">
                  <p>
                    Already have account? <Link to={"/"}>Sign In</Link>
                  </p>
                </div>
                <img
                  width={"300px"}
                  height={"300px"}
                  src={GamingBoardBahamasLogo}
                  className="booksAnimation"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginMfa;
